import { createContext, useContext, useMemo } from "react";
import { JSONPath } from "jsonpath-plus";
import uniqBy from "lodash.uniqby";
import { TypographyContent } from "../atoms";

export type TranslateFn = (
  key: string,
  context?: TranslateContext,
  opts?: TranslateOptions,
) => string;
export type TranslateContext = Record<string, unknown>;
export interface TranslateOptions {
  skipInterpolation?: boolean;
  skipPseudoKeys?: boolean;
  noFallback?: boolean;
  additionalScopes?: string[];
  overrideKeys?: string[];
}
export interface TranslateUtils {
  prefixes: string[];
  scopes: string[];
  localDateFns: { [key: string]: any };
  keySeparator: string;
  joinLocalKeys: (...keys: string[]) => string;
}

export const PSEUDO_KEY_EMPTY = "<empty />";

export const LocalizationContext = createContext(null);

export function useTranslate(
  stringOverrides?: Record<string, string>,
): [TranslateFn, Partial<TranslateUtils>] {
  const context = useContext(LocalizationContext);
  if (!context) return [null, {}];
  const { strings, scopes, localDateFns, keySeparator, onMissingKey } = context;
  return useMemo(
    () =>
      makeScopedTranslate(
        { ...strings, ...stringOverrides },
        scopes,
        keySeparator,
        {
          localDateFns,
          keySeparator,
          joinLocalKeys: joinLocalKeys(keySeparator),
        },
        onMissingKey,
      ),
    [strings, stringOverrides, scopes, localDateFns, keySeparator, onMissingKey],
  );
}

function makeScopedTranslate(
  strings,
  scopes,
  keySeparator,
  expose: Partial<TranslateUtils> = {},
  onMissingKey?: (entry: { key: string; keys: string[] }) => void,
): [TranslateFn, Partial<TranslateUtils>] {
  const resolve: TranslateFn = (key, context, opts) => {
    const template = strings[key];
    if (!template) return null;
    const result = opts.skipInterpolation
      ? template
      : applyContext(template, context);
    if (!opts.skipPseudoKeys) {
      if (result === PSEUDO_KEY_EMPTY) return " ";
    }
    return result;
  };
  const prefixes = resolveScopeChain(keySeparator)(scopes);

  const t: TranslateFn = (key, context, opts = {}) => {
    if (!key) return "";
    if (!strings) return key;

    let finalPrefixes = prefixes;
    if (opts.additionalScopes)
      finalPrefixes = resolveScopeChain(keySeparator)([
        ...opts.additionalScopes,
        ...scopes,
      ]);

    const keys =
      opts.overrideKeys || buildKeys(finalPrefixes, key, keySeparator);

    let result = keys.reduce((acc, k) => {
      if (acc) return acc;
      return resolve(k, context, opts);
    }, null);

    if (result) return result;

    onMissingKey?.({ key, keys });
    logTranslateWarning(keys);
    return opts.noFallback ? null : `[${key}]`;
  };

  return [t, { ...expose, prefixes, scopes }];
}

export function isRootLocalKey(localKey, keySeparator) {
  return localKey.startsWith(keySeparator);
}

export function makeRootLocalKey(localKey, keySeparator) {
  if (typeof localKey !== "string" || localKey.length === 0) return null;
  if (localKey.startsWith(keySeparator)) return localKey;
  return keySeparator + localKey;
}

export function joinLocalKeys(keySeparator) {
  return (...keys) => keys.filter((k) => !!k).join(keySeparator);
}

function resolveDataInterpolation(attrStr, context) {
  const queryMatch = attrStr.match(/query="(.*?)"/i);
  return queryMatch ? JSONPath({ path: queryMatch[1], json: context }) : "";
}

export function applyContext(template, context = {}) {
  return template
    .replace(/<data(.*?) \/>/gi, (_, attrStr) =>
      resolveDataInterpolation(attrStr, context),
    )
    .trim();
}

export function wrapIconData({ name }) {
  return name ? `<icon name="${name}" />` : "";
}

export function unwrapIcon(wrapped) {
  if (!wrapped?.startsWith("<icon")) return null;
  const nameMatch = wrapped && wrapped.match(/name="(.*?)"/i);
  return { name: nameMatch ? nameMatch[1] : null };
}

function stripSeparator(keySeparator) {
  return (key) => {
    if (key?.startsWith?.(keySeparator)) return key.slice(keySeparator.length);
    return key;
  };
}

function resolveScopeChain(keySeparator) {
  const resolveRecursive = (scopes) => {
    if (scopes.length === 0) return [];
    if (scopes.length === 1)
      return scopes[0].filter((s) => !!s).map(stripSeparator(keySeparator));
    const scope = scopes[0];
    const remaining = scopes.slice(1);

    return uniqBy(
      scope.reduce((acc, key) => {
        if (typeof key !== "string" || key.length === 0)
          return [...acc, ...resolveRecursive(remaining)];
        if (key.startsWith(keySeparator))
          return [...acc, stripSeparator(keySeparator)(key)];
        return [
          ...acc,
          ...resolveRecursive(remaining).map((c) => c + keySeparator + key),
        ];
      }, []),
    );
  };
  return resolveRecursive;
}

function logTranslateWarning(keys) {
  console.warn(`Missing localization for any of ${JSON.stringify(keys)}`);
}

export function buildKeys(prefixes, key, keySeparator) {
  if (key.startsWith(keySeparator)) return [stripSeparator(keySeparator)(key)];
  if (prefixes.length === 0) return [key];
  return prefixes.map((prefix) => {
    if (prefix.length === 0) return key;
    return prefix + keySeparator + key;
  });
}

export function addLocalContext(
  content: TypographyContent,
  context: object,
): TypographyContent {
  if (!content) return content;
  if (typeof content !== "object" || !("local" in content)) return content;

  const localContent = content.local;

  // If local is a LocalContentObject with existing context
  if (typeof localContent === "object" && "context" in localContent) {
    return {
      local: {
        ...localContent,
        context: { ...localContent.context, ...context },
      },
    };
  }

  // If local is boolean or string, upgrade to LocalContentObject with context
  return {
    local: {
      key: typeof localContent === "string" ? localContent : null,
      context,
    },
  };
}
